import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from '../../contexts/LocationContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useAuth } from '../../contexts/AuthContext';
import { warehousesService } from '../../services/warehousesService';
import { warehousePricingService } from '../../services/warehousePricingService';
import { productService } from '../../services/productService';
import manufacturerService from '../../services/manufacturerService';
import unitService from '../../services/unitService';
import { invoiceService } from '../../services/invoiceService';
import api from '../../services/api';

// Backend /warehouses/<id>/prices stronicuje (domyslnie 100/strone, max 500).
// Serwis warehousePricingService nie przekazuje parametrow paginacji, wiec
// pobieramy tu wszystkie strony bezposrednio przez klienta api.
// Zwraca null gdy pierwsza strona sie nie powiedzie (zachowujemy stare dane).
const fetchAllWarehousePrices = async (locId) => {
  const perPage = 500;
  let page = 1;
  let totalPages = 1;
  const allPrices = [];

  do {
    const response = await api.get(`/warehouses/${locId}/prices`, {
      params: { page, per_page: perPage }
    });
    const body = response.data;
    if (!body?.success) {
      return page === 1 ? null : allPrices;
    }
    const pagePrices = body.data?.prices || [];
    allPrices.push(...pagePrices);
    totalPages = body.data?.pagination?.total_pages || 1;
    if (pagePrices.length < perPage) break;
    page += 1;
  } while (page <= totalPages);

  return allPrices;
};

// MARZA = zysk / cena sprzedazy netto (NIGDY narzut = zysk / koszt).
// Zwraca null gdy brak danych do policzenia (sell<=0 lub buy<=0).
const computeMarginPercent = (sellNetto, buyNetto) => {
  const sell = parseFloat(sellNetto) || 0;
  const buy = parseFloat(buyNetto) || 0;
  if (sell <= 0 || buy <= 0) return null;
  return ((sell - buy) / sell) * 100;
};

const usePricingData = () => {
  const { selectedLocation, availableLocations, changeLocation } = useLocation();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();

  // Uprawnienia
  const locationId = selectedLocation?.id;
  const canEditPrices = hasPermission('cenowki', 'edycja', locationId);
  const canPrintLabels = hasPermission('cenowki', 'drukowanie', locationId);
  const canViewHistory = hasPermission('cenowki', 'historia', locationId);
  const canDeleteSpecialPrice = hasPermission('cenowki', 'usun_cene_specjalna', locationId);

  // Core data state
  const [locationPrices, setLocationPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [measureUnits, setMeasureUnits] = useState([]);
  const [warehouseId, setWarehouseId] = useState(null);
  // Cenowki domyslnie pokazuja tylko towar ZE STANEM (available_only=1) - tak jest
  // szybciej i o to chodzi przy drukowaniu etykiet. Ale wtedy wyszukiwarka nie
  // widzi produktow bez stanu (na prodzie: 2592 aktywne karty, ze stanem 608-982
  // na sklep), wiec kasjer szukajac czegos wyprzedanego dostawal 'brak wynikow'.
  // Ten przelacznik pozwala dolaczyc caly asortyment.
  const [pokazBezStanu, setPokazBezStanuState] = useState(false);
  // Ref, zeby loadAllProducts (useCallback bez tej zaleznosci) nie czytalo
  // starej wartosci po przelaczeniu checkboxa.
  const pokazBezStanuRef = useRef(false);
  const setPokazBezStanu = useCallback((wartosc) => {
    pokazBezStanuRef.current = !!wartosc;
    setPokazBezStanuState(!!wartosc);
  }, []);

  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [invoiceEANs, setInvoiceEANs] = useState(new Set());
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // --- Data loading functions ---

  const loadWarehouseForLocation = async (locId) => {
    try {
      const warehouses = await warehousesService.getWarehouses();
      const warehouse = warehouses.find(w => String(w.location_id) === String(locId));
      if (warehouse) {
        setWarehouseId(warehouse.id);
      } else {
        setWarehouseId(null);
      }
    } catch (err) {
      console.error('Błąd pobierania magazynu dla lokalizacji:', err);
      setWarehouseId(null);
    }
  };

  const loadManufacturers = async () => {
    try {
      const manufacturersData = await manufacturerService.getManufacturers();
      setManufacturers(manufacturersData || []);
    } catch (err) {
      console.error('❌ Błąd ładowania producentów:', err);
    }
  };

  const loadUnits = async () => {
    try {
      const unitsData = await unitService.getActiveUnits();
      setMeasureUnits(unitsData || []);
    } catch (err) {
      console.error('❌ Błąd ładowania jednostek miary:', err);
    }
  };

  const loadInvoices = async () => {
    try {
      // Faktury TEJ lokalizacji (nie z sesji): pozycje faktury z innego sklepu
      // nigdy nie pasowaly do magazynu, wiec lista "z faktury" byla pusta lub ucieta.
      const response = await invoiceService.getInvoices({ limit: 100, location_id: selectedLocation?.id });
      let invoicesData = [];
      if (response.success && response.data && response.data.invoices) {
        invoicesData = response.data.invoices;
      } else if (response.invoices) {
        invoicesData = response.invoices;
      }
      setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
    } catch (err) {
      console.error('Błąd ładowania faktur:', err);
      setInvoices([]);
    }
  };

  const loadLocationPrices = async (locId) => {
    try {
      setLoading(true);
      const response = await warehousePricingService.getLocationPrices(locId);
      if (response.success) {
        const pricesArray = response.data?.prices || [];
        setLocationPrices(pricesArray);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAllProducts = useCallback(async (forceRefreshPrices = false, opcje = {}) => {
    if (!selectedLocation) return;

    try {
      setLoading(true);

      let currentLocationPrices = locationPrices;
      if (forceRefreshPrices) {
        // Pobierz WSZYSTKIE strony cen (nie tylko pierwsza 100/1911)
        const refreshedPrices = await fetchAllWarehousePrices(selectedLocation.id);
        if (refreshedPrices !== null) {
          currentLocationPrices = refreshedPrices;
          setLocationPrices(refreshedPrices);
        }
      }

      // availableOnly: false = takze produkty bez stanu (cale karty aktywne)
      const zeStanemTylko = opcje.pokazBezStanu !== undefined
        ? !opcje.pokazBezStanu
        : !pokazBezStanuRef.current;
      const inventoryResponse = await productService.getInventoryProducts({
        locationId: selectedLocation.id,
        limit: 5000,
        availableOnly: zeStanemTylko
      });
      const products = inventoryResponse.products || [];

      const pricesByProductId = new Map(
        (currentLocationPrices || []).map(lp => [lp.product_id, lp])
      );

      const productsWithPrices = products.map(product => {
        const locationPrice = pricesByProductId.get(product.id);

        // Kontrakt pol: /warehouses/<id>/prices zwraca warehouse_price_net /
        // special_price, a /locations/<id>/prices zwraca cena_sprzedazy_netto /
        // cena_sprzedazy_brutto (same wpisy specjalne) - czytaj oba warianty.
        const lpNetto = locationPrice?.warehouse_price_net ?? locationPrice?.cena_sprzedazy_netto ?? null;
        const lpBrutto = locationPrice?.special_price ?? locationPrice?.warehouse_price ?? locationPrice?.cena_sprzedazy_brutto ?? null;
        const hasSpecialPrice = locationPrice
          ? (locationPrice.has_special_price !== undefined ? !!locationPrice.has_special_price : true)
          : false;
        const specialPriceNetto = hasSpecialPrice && lpNetto !== null ? parseFloat(lpNetto) : null;
        const specialPriceBrutto = hasSpecialPrice && lpBrutto !== null ? parseFloat(lpBrutto) : null;
        const purchasePriceFromAPI = parseFloat(locationPrice?.purchase_price) || parseFloat(product.cena_zakupu_netto) || 0;

        // Kaucja: wykluczona z marzy (marza = null, bez liczenia)
        const isDeposit = product.typ_produktu === 'kaucja' || parseFloat(product.stawka_vat) === -2;

        // Preferuj marze z API per-lokalizacja (backend liczy z FIFO, ÷sprzedaz);
        // brak wartosci z API => null (NIE 0), wtedy liczymy sami poprawna formula.
        const apiMarginRaw = locationPrice?.margin;
        const apiMargin = (apiMarginRaw === null || apiMarginRaw === undefined || isDeposit)
          ? null : parseFloat(apiMarginRaw);

        let specialMargin = null;
        if (hasSpecialPrice && !isDeposit) {
          const specialPercent = apiMargin !== null
            ? apiMargin
            : computeMarginPercent(specialPriceNetto, purchasePriceFromAPI);
          const specialAmount = specialPriceNetto && purchasePriceFromAPI ?
            (specialPriceNetto - purchasePriceFromAPI) : 0;
          specialMargin = {
            percent: specialPercent !== null ? Math.round(specialPercent) : 0,
            amount: parseFloat(specialAmount.toFixed(2))
          };
        }

        const defaultPriceNetto = parseFloat(product.cena_sprzedazy_netto) || 0;
        const defaultMarginAmount = defaultPriceNetto && purchasePriceFromAPI ?
          (defaultPriceNetto - purchasePriceFromAPI) : 0;
        const defaultMarginPercent = isDeposit ? null :
          computeMarginPercent(defaultPriceNetto, purchasePriceFromAPI);

        const defaultMargin = {
          percent: defaultMarginPercent !== null ? Math.round(defaultMarginPercent) : 0,
          amount: parseFloat(defaultMarginAmount.toFixed(2))
        };

        // Jedno zrodlo marzy dla kolumny tabeli, sortowania i filtra progu
        const displayMarginPercent = isDeposit ? null :
          (apiMargin !== null ? apiMargin :
            (hasSpecialPrice
              ? computeMarginPercent(specialPriceNetto, purchasePriceFromAPI)
              : defaultMarginPercent));

        return {
          ...product,
          hasSpecialPrice,
          specialPriceNetto,
          specialPriceBrutto,
          priceDiffPercent: hasSpecialPrice && specialPriceBrutto && parseFloat(product.cena_sprzedazy_brutto) > 0 ?
            Math.round(((specialPriceBrutto - product.cena_sprzedazy_brutto) / product.cena_sprzedazy_brutto) * 100) : 0,
          defaultMargin,
          specialMargin,
          purchase_price: purchasePriceFromAPI,
          margin_from_api: apiMargin,
          displayMarginPercent,
          isDeposit,
          margin_method: locationPrice?.margin_method || null
        };
      });

      setAllProducts(productsWithPrices);

      const uniqueCategories = [...new Set(productsWithPrices.map(p => p.kategoria).filter(Boolean))];
      setCategories(uniqueCategories.sort());

    } catch (err) {
      console.error('Błąd ładowania produktów:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, locationPrices]);

  const refreshAllData = async () => {
    if (!selectedLocation) return;
    await loadLocationPrices(selectedLocation.id);
    await loadAllProducts(true);
  };

  // Invoice filter handler
  const handleInvoiceFilter = async (invoiceId) => {
    setSelectedInvoiceId(invoiceId);

    if (!invoiceId) {
      setInvoiceEANs(new Set());
      return;
    }

    try {
      setInvoiceLoading(true);
      // Lista z faktury ma pokazac WSZYSTKIE jej pozycje - takze wyprzedane
      // (stan 0). Domyslny filtr "Dostepne" ukrywal je, wiec z 7 pozycji
      // widac bylo 2 (zgloszenie z Leszna, 2026-09-11).
      if (!pokazBezStanuRef.current) {
        setPokazBezStanu(true);
        await loadAllProducts(false, { pokazBezStanu: true });
      }
      const response = await invoiceService.getInvoiceDetails(invoiceId);

      if (response.success || response.data) {
        const invoiceData = response.data || response;
        const items = invoiceData.items || [];

        const eansFromInvoice = new Set();
        items.forEach(item => {
          if (item.ean && item.ean.trim()) {
            eansFromInvoice.add(item.ean.trim());
          }
          if (item.kod_produktu && item.kod_produktu.trim()) {
            eansFromInvoice.add(item.kod_produktu.trim());
          }
        });

        setInvoiceEANs(eansFromInvoice);
      }
    } catch (err) {
      console.error('Błąd ładowania faktury:', err);
      setInvoiceEANs(new Set());
    } finally {
      setInvoiceLoading(false);
    }
  };

  // --- Effects ---

  useEffect(() => {
    loadManufacturers();
    loadUnits();
  }, []);

  // Auto-detect manufacturers for products
  useEffect(() => {
    if (manufacturers.length > 0 && allProducts.length > 0) {
      const detectManufacturer = (name) => {
        if (!name) return '';
        const upperName = name.toUpperCase();
        for (const mfr of manufacturers) {
          if (upperName.includes(mfr.nazwa.toUpperCase())) {
            return mfr.nazwa;
          }
        }
        return '';
      };

      const needsUpdate = allProducts.some(p => !p.manufacturer && p.nazwa);

      if (needsUpdate) {
        const updatedProducts = allProducts.map(product => ({
          ...product,
          manufacturer: product.manufacturer || detectManufacturer(product.nazwa)
        }));
        setAllProducts(updatedProducts);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manufacturers]);

  // Load data when location changes
  useEffect(() => {
    if (selectedLocation) {
      loadManufacturers();
      loadUnits();
      loadAllProducts(true);
      loadInvoices();
      loadWarehouseForLocation(selectedLocation.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  return {
    // Context
    selectedLocation,
    availableLocations,
    changeLocation,
    user,

    // Permissions
    canEditPrices,
    canPrintLabels,
    canViewHistory,
    canDeleteSpecialPrice,

    // Core data
    locationPrices,
    loading,
    setLoading,
    error,
    setError,
    allProducts,
    setAllProducts,
    categories,
    manufacturers,
    measureUnits,
    warehouseId,

    // Invoice data
    invoices,
    selectedInvoiceId,
    invoiceEANs,
    invoiceLoading,
    handleInvoiceFilter,

    // Actions
    loadAllProducts,
    pokazBezStanu,
    setPokazBezStanu,
    loadLocationPrices,
    refreshAllData,
    loadManufacturers,
    loadUnits,
  };
};

export default usePricingData;
